import React, { useState, useCallback, useMemo } from 'react';
import { getFixtureCenter } from '@amzn/ihm-floorplan-core';
import { ZoneCreationProperties } from '../types/zoneMapType';
import { PickPath, Waypoint } from '../pickPath/pickPathTypes';
import { buildPickPathFeatureCollection, PickPathFeatureCollection } from '../pickPath/buildPickPathFeatureCollection';
import { FloorplanData } from '../zoneViewer/zoneMapDataType';

export interface UsePickPathDrawingProps {
  zoneCreationProperties: Map<number, ZoneCreationProperties>;
  setZoneCreationProperties: React.Dispatch<React.SetStateAction<Map<number, ZoneCreationProperties>>>;
  activeZoneIdx: number;
  fixtureData: FloorplanData[];
}

export interface UsePickPathDrawingReturn {
  isDrawing: boolean;
  startDrawing: () => void;
  stopDrawing: () => void;
  inProgressPath: PickPath | null;
  addWaypoint: (fixtureId: string) => void;
  finishPath: () => boolean;
  pickPathFeatureCollection: PickPathFeatureCollection;
  // Drag-to-draw
  isDragging: boolean;
  onPointerDown: (fixtureId: string) => void;
  onPointerUp: () => void;
  handleFixturePointerOver: (fixtureId: string) => void;
  // Undo
  undoLastWaypoint: () => void;
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

export function usePickPathDrawing({
  zoneCreationProperties,
  setZoneCreationProperties,
  activeZoneIdx,
  fixtureData,
}: UsePickPathDrawingProps): UsePickPathDrawingReturn {
  const [isDrawing, setIsDrawing] = useState(false);
  const [inProgressPath, setInProgressPath] = useState<PickPath | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Refs to avoid stale closures in callbacks
  const isDrawingRef = React.useRef(isDrawing);
  isDrawingRef.current = isDrawing;
  const inProgressPathRef = React.useRef(inProgressPath);
  inProgressPathRef.current = inProgressPath;
  const isDraggingRef = React.useRef(false);

  // Build a fixture lookup map for resolving centers
  const fixtureMap = useMemo(() => {
    const map = new Map<string, FloorplanData>();
    for (const f of fixtureData) {
      if (f.id) map.set(f.id, f);
    }
    return map;
  }, [fixtureData]);

  // Build zone color map for rendering
  const zoneColorMap = useMemo(() => {
    const map = new Map<number, string>();
    zoneCreationProperties.forEach((_zone, idx) => {
      map.set(idx, '#FF0000');
    });
    return map;
  }, [zoneCreationProperties]);

  // Helper: get all paths across all zones
  const allPaths = useMemo(() => {
    const paths: PickPath[] = [];
    zoneCreationProperties.forEach((zone) => {
      if (zone.pickPaths) paths.push(...zone.pickPaths);
    });
    return paths;
  }, [zoneCreationProperties]);

  const startDrawing = useCallback(() => {
    if (!zoneCreationProperties.has(activeZoneIdx)) return;
    setIsDrawing(true);
    setInProgressPath({
      id: generateId(),
      zoneIdx: activeZoneIdx,
      waypoints: [],
    });
  }, [activeZoneIdx, zoneCreationProperties]);

  const stopDrawing = useCallback(() => {
    setIsDrawing(false);
    setInProgressPath(null);
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  // Build set of all fixture IDs already used in completed paths (for cross-path dedup)
  const usedFixtureIds = useMemo(() => {
    const ids = new Set<string>();
    allPaths.forEach((path) => path.waypoints.forEach((wp) => ids.add(wp.fixtureId)));
    return ids;
  }, [allPaths]);

  const addWaypoint = useCallback(
    (fixtureId: string) => {
      if (!isDrawingRef.current || !inProgressPathRef.current) {
        return;
      }

      const fixture = fixtureMap.get(fixtureId);
      if (!fixture) return;

      // Reject fixture already in the current in-progress path
      const currentPath = inProgressPathRef.current;
      if (currentPath.waypoints.some((wp) => wp.fixtureId === fixtureId)) return;

      // Reject fixture already used in any completed path
      if (usedFixtureIds.has(fixtureId)) return;

      const center = getFixtureCenter(fixture);
      const newWaypoint: Waypoint = {
        fixtureId,
        x: center[0],
        y: center[1],
      };

      setInProgressPath((prev) => (prev ? { ...prev, waypoints: [...prev.waypoints, newWaypoint] } : prev));
    },
    [fixtureMap, usedFixtureIds],
  );

  const onPointerDown = useCallback(
    (fixtureId: string) => {
      if (!isDrawingRef.current) return;
      isDraggingRef.current = true;
      setIsDragging(true);
      addWaypoint(fixtureId);
    },
    [addWaypoint],
  );

  const onPointerUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  const handleFixturePointerOver = useCallback(
    (fixtureId: string) => {
      if (!isDraggingRef.current || !isDrawingRef.current) return;
      addWaypoint(fixtureId);
    },
    [addWaypoint],
  );

  const undoLastWaypoint = useCallback(() => {
    if (!inProgressPathRef.current || inProgressPathRef.current.waypoints.length === 0) return;
    setInProgressPath((prev) => (prev ? { ...prev, waypoints: prev.waypoints.slice(0, -1) } : prev));
  }, []);

  const finishPath = useCallback((): boolean => {
    const currentInProgress = inProgressPathRef.current;
    if (!currentInProgress || currentInProgress.waypoints.length < 2) {
      return false;
    }

    const completedPath = { ...currentInProgress };
    const currentPaths = zoneCreationProperties.get(activeZoneIdx)?.pickPaths ?? [];
    setZoneCreationProperties((prev) => {
      const newMap = new Map(prev);
      const zone = prev.get(activeZoneIdx);
      if (!zone) return prev;
      newMap.set(activeZoneIdx, { ...zone, pickPaths: [...currentPaths, completedPath] });
      return newMap;
    });

    // Stay in drawing mode with a fresh in-progress path so the user can continue
    setInProgressPath({
      id: generateId(),
      zoneIdx: activeZoneIdx,
      waypoints: [],
    });
    return true;
  }, [activeZoneIdx, zoneCreationProperties, setZoneCreationProperties]);

  // Compute the feature collection for rendering
  const pickPathFeatureCollection = useMemo(
    () => buildPickPathFeatureCollection(allPaths, inProgressPath, zoneColorMap),
    [allPaths, inProgressPath, zoneColorMap],
  );

  return {
    isDrawing,
    startDrawing,
    stopDrawing,
    inProgressPath,
    addWaypoint,
    finishPath,
    pickPathFeatureCollection,
    isDragging,
    onPointerDown,
    onPointerUp,
    handleFixturePointerOver,
    undoLastWaypoint,
  };
}
